/* eslint-disable sort-keys */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import UseContentful from '@ecm/capability/UseContentful'
import { ContentQuery, ContentQueryOperators, ContentType } from '@ecm/data/Content'
import { Entry, EntryCollection } from '@ecm/data/ContentfulTypes'

import addonData from '../../../data/contentful/addon.json'
// Static imports of all content JSON files
import addressData from '../../../data/contentful/address.json'
import announcementData from '../../../data/contentful/announcement.json'
import areaData from '../../../data/contentful/area.json'
import articleData from '../../../data/contentful/article.json'
import articleCategoryData from '../../../data/contentful/articleCategory.json'
import authorData from '../../../data/contentful/author.json'
import caseStudiesData from '../../../data/contentful/caseStudies.json'
import couponCodeData from '../../../data/contentful/couponCode.json'
import faqData from '../../../data/contentful/faq.json'
import landingPageData from '../../../data/contentful/landingPage.json'
import locationData from '../../../data/contentful/location.json'
import partnerData from '../../../data/contentful/partner.json'
import planData from '../../../data/contentful/plan.json'
import snippetData from '../../../data/contentful/snippet.json'
import testimonialData from '../../../data/contentful/testimonial.json'

/**
 * Static implementation of UseContentful that reads from local JSON files
 * instead of calling the Contentful API.
 *
 * This implementation:
 * - Imports JSON files statically at build time
 * - Supports all the same query operations as the real Contentful client
 * - Returns data in the same format as the Contentful SDK
 * - Works at build time (SSG) and runtime (SSR/client)
 */

// Map content types to their imported data
const contentTypeDataMap: Record<string, any> = {
  address: addressData,
  addon: addonData,
  announcement: announcementData,
  area: areaData,
  article: articleData,
  articleCategory: articleCategoryData,
  author: authorData,
  caseStudies: caseStudiesData,
  couponCode: couponCodeData,
  faq: faqData,
  landingPage: landingPageData,
  location: locationData,
  partner: partnerData,
  plan: planData,
  snippet: snippetData,
  testimonial: testimonialData,
}

function loadContentType(contentType: ContentType): any {
  const data = contentTypeDataMap[contentType]

  if (!data) {
    console.warn(`No static data found for content type: ${contentType}`)
    return { items: [], total: 0, includes: {} }
  }

  return data
}

function matchesQuery(entry: Entry<any>, query?: ContentQuery): boolean {
  // If no query, match all
  if (!query) {return true}

  // Note: order and pagination are handled separately
  // This function only handles field matching
  return true
}

function applyOrder(items: Entry<any>[], order?: string): Entry<any>[] {
  if (!order) {return items}

  const [field, direction] = order.startsWith('-')
    ? [order.substring(1), 'desc']
    : [order, 'asc']

  const fieldParts = field.split('.')
  const isFieldsPath = fieldParts[0] === 'fields'
  const fieldName = isFieldsPath ? fieldParts.slice(1).join('.') : field

  return [...items].sort((a, b) => {
    const aVal = isFieldsPath ? (a.fields as any)?.[fieldName] : (a as any)[fieldName]
    const bVal = isFieldsPath ? (b.fields as any)?.[fieldName] : (b as any)[fieldName]

    if (aVal === bVal) {return 0}

    const comparison = aVal > bVal ? 1 : -1
    return direction === 'desc' ? -comparison : comparison
  })
}

function applyPagination(items: Entry<any>[], query?: ContentQuery): Entry<any>[] {
  const skip = query?.skip || 0
  const limit = query?.limit || items.length

  return items.slice(skip, skip + limit)
}

export const staticUseContentful: UseContentful = {
  _client: undefined,
  _previewClient: undefined,

  getClient() {
    throw new Error('Static mode does not use Contentful client')
  },

  getPreviewClient() {
    throw new Error('Static mode does not support preview')
  },

  async getEntries<R, S>(
    contentType: ContentType,
    parser: (c: Entry<R>) => S,
    query?: ContentQuery,
    isPreview?: boolean
  ): Promise<S[]> {
    if (isPreview) {
      console.warn('Preview mode not supported in static mode')
    }

    const data = loadContentType(contentType)
    let items = data.items.filter((item: Entry<R>) => matchesQuery(item, query))

    items = applyOrder(items, query?.order)
    items = applyPagination(items, query)

    return items.map(parser)
  },

  async getEntry<R, S>(id: string, parser: (c: Entry<R>) => S): Promise<S> {
    // We need to search across all content types for this ID
    // This is inefficient but matches Contentful's behavior
    throw new Error('getEntry by ID not yet implemented in static mode. Use getEntryBySlug instead.')
  },

  async getEntriesByFieldValues<R, S>(
    contentType: ContentType,
    field: string,
    values: string[],
    parser: (c: Entry<R>) => S,
    operator: ContentQueryOperators = '[all]',
    query?: ContentQuery,
    isPreview?: boolean
  ): Promise<S[]> {
    if (isPreview) {
      console.warn('Preview mode not supported in static mode')
    }

    const data = loadContentType(contentType)
    let items = data.items.filter((item: Entry<R>) => {
      const fieldValue = (item.fields as any)?.[field]

      switch (operator) {
        case '[all]':
          return Array.isArray(fieldValue)
            ? values.every(v => fieldValue.includes(v))
            : values.includes(fieldValue)
        case '[in]':
          return values.includes(fieldValue)
        case '[nin]':
          return !values.includes(fieldValue)
        case '[gte]':
          return fieldValue >= values[0]
        case '[gt]':
          return fieldValue > values[0]
        case '[lte]':
          return fieldValue <= values[0]
        case '[lt]':
          return fieldValue < values[0]
        case '[ne]':
          return fieldValue !== values[0]
        case '[exists]':
          return values[0] === 'true' ? fieldValue !== undefined : fieldValue === undefined
        default:
          return true
      }
    })

    if (!matchesQuery(items[0], query)) {
      items = items.filter((item: Entry<R>) => matchesQuery(item, query))
    }

    items = applyOrder(items, query?.order)
    items = applyPagination(items, query)

    return items.map(parser)
  },

  async getEntriesByLinkedEntry<R, S>(
    contentType: ContentType,
    linkedEntryId: string,
    parser: (c: Entry<R>) => S,
    query?: ContentQuery,
    isPreview?: boolean
  ): Promise<S[]> {
    if (isPreview) {
      console.warn('Preview mode not supported in static mode')
    }

    const data = loadContentType(contentType)

    // Find entries that link to the specified entry
    let items = data.items.filter((item: Entry<R>) => {
      // Check all fields for links to the target entry
      return Object.values(item.fields || {}).some((fieldValue: any) => {
        // Check if it's a linked entry
        if (fieldValue?.sys?.id === linkedEntryId) {return true}

        // Check if it's an array of linked entries
        if (Array.isArray(fieldValue)) {
          return fieldValue.some(val => val?.sys?.id === linkedEntryId)
        }

        return false
      })
    })

    items = applyOrder(items, query?.order)
    items = applyPagination(items, query)

    return items.map(parser)
  },

  async getEntriesByLinkedEntryWithField<R, S>(
    contentType: ContentType,
    field: string,
    parser: (c: Entry<R>) => S,
    linkedEntryId?: string,
    query?: ContentQuery,
    isPreview?: boolean
  ): Promise<S[]> {
    if (isPreview) {
      console.warn('Preview mode not supported in static mode')
    }

    const data = loadContentType(contentType)

    let items = data.items.filter((item: Entry<R>) => {
      const fieldValue = (item.fields as any)?.[field]
      if (fieldValue === undefined) {return false}

      if (!linkedEntryId) {return true}

      // Check if field links to the specified entry
      if (fieldValue?.sys?.id === linkedEntryId) {return true}

      // Check if it's an array containing the linked entry
      if (Array.isArray(fieldValue)) {
        return fieldValue.some(val => val?.sys?.id === linkedEntryId)
      }

      return false
    })

    items = applyOrder(items, query?.order)
    items = applyPagination(items, query)

    return items.map(parser)
  },

  async getEntryBySlug<R, S>(
    contentType: ContentType,
    slug: string,
    parser: (c: Entry<R>) => S,
    isPreview?: boolean
  ): Promise<S> {
    if (isPreview) {
      console.warn('Preview mode not supported in static mode')
    }

    const data = loadContentType(contentType)
    const entry = data.items.find((item: Entry<R>) => (item.fields as any)?.slug === slug)

    if (!entry) {
      throw new Error(`Entry with slug "${slug}" not found in ${contentType}`)
    }

    return parser(entry)
  },

  async getEntryBySlugAndAreaSlug<R, S>(
    contentType: ContentType,
    slug: string,
    areaSlug: string,
    parser: (c: Entry<R>) => S,
    isPreview?: boolean
  ): Promise<S> {
    if (isPreview) {
      console.warn('Preview mode not supported in static mode')
    }

    const data = loadContentType(contentType)
    const entry = data.items.find(
      (item: Entry<R>) => (item.fields as any)?.slug === slug && (item.fields as any)?.areaSlug === areaSlug
    )

    if (!entry) {
      throw new Error(
        `Entry with slug "${slug}" and areaSlug "${areaSlug}" not found in ${contentType}`
      )
    }

    return parser(entry)
  },

  async getEntryMetadata<R, S>(
    contentType: ContentType,
    parser: (c: EntryCollection<R>) => S
  ): Promise<S> {
    const data = loadContentType(contentType)

    const metadata: EntryCollection<R> = {
      sys: { type: 'Array' },
      total: data.total,
      skip: 0,
      limit: 0,
      items: [],
    }

    return parser(metadata)
  },

  async getEntryMetadataByFieldValue<R, S>(
    contentType: ContentType,
    field: string,
    parser: (c: EntryCollection<R>) => S
  ): Promise<S> {
    const data = loadContentType(contentType)

    const items = data.items.filter((item: Entry<R>) => (item.fields as any)?.[field] !== undefined)

    const metadata: EntryCollection<R> = {
      sys: { type: 'Array' },
      total: items.length,
      skip: 0,
      limit: 0,
      items: [],
    }

    return parser(metadata)
  },

  async getEntryMetadataById<R, S>(
    contentType: ContentType,
    linkedEntryId: string,
    parser: (c: EntryCollection<R>) => S
  ): Promise<S> {
    const data = loadContentType(contentType)

    const items = data.items.filter((item: Entry<R>) => {
      return Object.values(item.fields || {}).some((fieldValue: any) => {
        if (fieldValue?.sys?.id === linkedEntryId) {return true}
        if (Array.isArray(fieldValue)) {
          return fieldValue.some(val => val?.sys?.id === linkedEntryId)
        }
        return false
      })
    })

    const metadata: EntryCollection<R> = {
      sys: { type: 'Array' },
      total: items.length,
      skip: 0,
      limit: 0,
      items: [],
    }

    return parser(metadata)
  },

  async getEntriesWithField<R, S>(
    contentType: ContentType,
    field: string,
    parser: (c: Entry<R>) => S
  ): Promise<S[]> {
    const data = loadContentType(contentType)

    const items = data.items.filter((item: Entry<R>) => (item.fields as any)?.[field] !== undefined)

    return items.map(parser)
  },

  async getPaginatedEntries<R, S>(
    contentType: ContentType,
    parser: (c: EntryCollection<R>) => S,
    query?: ContentQuery,
    isPreview?: boolean
  ): Promise<S> {
    if (isPreview) {
      console.warn('Preview mode not supported in static mode')
    }

    const data = loadContentType(contentType)
    let items = data.items.filter((item: Entry<R>) => matchesQuery(item, query))

    items = applyOrder(items, query?.order)

    const total = items.length
    items = applyPagination(items, query)

    const collection: EntryCollection<R> = {
      sys: { type: 'Array' },
      total,
      skip: query?.skip || 0,
      limit: query?.limit || items.length,
      items,
    }

    return parser(collection)
  },

  async getPaginatedEntriesByLinkedEntry<R, S>(
    contentType: ContentType,
    linkedEntryId: string,
    parser: (c: EntryCollection<R>) => S,
    query?: ContentQuery,
    isPreview?: boolean
  ): Promise<S> {
    if (isPreview) {
      console.warn('Preview mode not supported in static mode')
    }

    const data = loadContentType(contentType)

    let items = data.items.filter((item: Entry<R>) => {
      return Object.values(item.fields || {}).some((fieldValue: any) => {
        if (fieldValue?.sys?.id === linkedEntryId) {return true}
        if (Array.isArray(fieldValue)) {
          return fieldValue.some(val => val?.sys?.id === linkedEntryId)
        }
        return false
      })
    })

    items = applyOrder(items, query?.order)

    const total = items.length
    items = applyPagination(items, query)

    const collection: EntryCollection<R> = {
      sys: { type: 'Array' },
      total,
      skip: query?.skip || 0,
      limit: query?.limit || items.length,
      items,
    }

    return parser(collection)
  },
}

export default staticUseContentful
